

import React, { useState, useEffect, useRef } from 'react';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from 'html2canvas';
import Heading from "../../components/ui/Heading";
import api from "../../api/api"; 

const Panel = ({ title, children }) => (
  <div className="p-4 mb-4 bg-white rounded-md shadow-sm">
    {title && <h2 className="mb-3 text-lg font-bold text-gray-700">{title}</h2>}
    {children}
  </div>
);

const Button = ({ children, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 text-sm text-white font-medium rounded-md shadow-sm transition duration-200 ease-in-out 
      ${disabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} 
      focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500`}
  >
    {children}
  </button>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-8">
    <svg className="w-6 h-6 mr-2 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
    <span className="text-gray-600 text-sm">Loading...</span>
  </div>
);

const Message = ({ text, type }) => {
  const color = type === 'error' ? 'text-red-500' : 'text-green-500';
  return <div className={`text-center py-3 text-sm ${color}`}>{text}</div>;
};

export default function CustomerLedgerPage() {
  const [partyTypes, setPartyTypes] = useState([]);
  const [parties, setParties] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({});
  const [selectedPartyType, setSelectedPartyType] = useState('');
  const [selectedParty, setSelectedParty] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date(); date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [reportFormat, setReportFormat] = useState('Ledger Statement');
  const [ledgerData, setLedgerData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [itemDetails, setItemDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const ledgerRef = useRef(null);
  const user = 'admin';

  const filteredCustomers = parties.filter(c =>
    c.VName.toLowerCase().includes(customerSearch.toLowerCase())
  );


useEffect(() => {
  const fetchParties = async () => {
    try {
      const res = await api.get("GetAccountledger", { user }); 
      const data = res.GetAccountledger || res;  

      setParties(data.Table || []); 
      setPartyTypes(data.Table1 || []); 
      setCompanyInfo(data.Table2 ? data.Table2[0] : {}); 

      if (data.Table1?.length) setSelectedPartyType(data.Table1[0].Vtype);
      if (data.Table?.length) setSelectedParty(data.Table[0]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  fetchParties();
}, []);
 const handleGenerate = async () => {
  setError('');
  if (!selectedParty) {
    setError('Please select a party.');
    return;
  }
  setLoading(true);
  setLedgerData([]);
  setItemDetails([]);

  try {
    // Correct API parameter names
    const res = await api.get("AccountLedgercuswiseAPI", {
      Vtype: selectedPartyType,      // Party type
      Vid: selectedParty.Vid,        // Selected party ID
      FromDate: fromDate,            // From date
      ToDate: toDate,                // To date
      Sessid: "9944010155",     // Replace with actual session ID
      Usrname: "usaer",               // Replace with actual username
      PrjId: 0,      // Optional if required
      Rptformat: reportFormat === "Detailed" ? "Detailed" : "Summary"
    });

    // Access the correct root object
    const data = res.AccountLedgercuswiseAPIjs || res;

    // Map ledger and item details
    const ledger = (data.Table || []).map(r => ({ ...r, Cpid: r.Cpid ? String(r.Cpid) : null }));
    const items  = (data.Table3 || []).map(i => ({ ...i, Cpid: i.Cpid ? String(i.Cpid) : null }));

    setLedgerData(ledger);
    setItemDetails(items);
    setSummaryData(data.Table1 ? data.Table1[0] : null);
    setShowResults(true);

  } catch (err) {
    setError(err.message || "Failed to fetch ledger data");
  } finally {
    setLoading(false);
  }
};


  const handleCustomerSelect = (customer) => {
    setSelectedParty(customer);
    setCustomerSearch(customer.VName);
    setIsDropdownOpen(false);
  };

 const exportLedgerToPDF = () => {
  if (!ledgerData.length) {
    alert("No ledger data to export.");
    return;
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // --- Header ---
  doc.setFontSize(16);
  doc.text(companyInfo.ComName || "Company Name", 14, 15);
  doc.setFontSize(10);
  doc.text(`${companyInfo.Address1 || ""}, ${companyInfo.Address2 || ""}`, 14, 21);
  doc.text(`${companyInfo.City || ""} - ${companyInfo.Pincode || ""}, ${companyInfo.State || ""}`, 14, 26);

  doc.setFontSize(14);
  doc.text(reportFormat, 14, 35);

  // --- Dates ---
  const fromText = `From: ${fromDate}`;
  const toText = `To: ${toDate}`;
  const toTextX = pageWidth - 14 - doc.getTextWidth(toText);
  const fromTextX = toTextX - 10 - doc.getTextWidth(fromText);
  doc.setFontSize(10);
  doc.text(fromText, fromTextX, 35);
  doc.text(toText, toTextX, 35);

  // --- Party ---
  if (selectedParty) {
    doc.setFontSize(12);
    doc.text(`Name: ${selectedParty.VName}`, 14, 42);
    doc.text(`Mobile: ${selectedParty.Mobile}`, 14, 48);
  }
  if (summaryData) {
    doc.setFontSize(12);
    const closingBalanceText = `Closing Balance: ${summaryData.Balance.toFixed(2)}`;
    doc.text(closingBalanceText, pageWidth - doc.getTextWidth(closingBalanceText) - 14, 48);
  }

  let currentY = 55;

  // Loop each ledger row
  ledgerData.forEach((row, idx) => {
    // --- Ledger Row ---
    autoTable(doc, {
      head: idx === 0 ? [["Date", "Particulars", "Invoice", "Receipt", "Balance"]] : undefined,
      body: [[
        row.RefDate,
        `${row.RefNo} / ${row.RefType} / ${row.Particulars}`,
        (row.Invoice || 0).toFixed(2),
        parseFloat(row.Receipt || 0).toFixed(2),
        (row.Balance || 0).toFixed(2),
      ]],
      startY: currentY,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
      theme: "grid"
    });

    currentY = doc.lastAutoTable.finalY + 2;

    // --- Item Details for Invoices ---
    if (reportFormat === "Detailed" && row.RefType === "Invoice") {
      const relatedItems = itemDetails.filter(item => item.Cpid === row.Cpid);
      if (relatedItems.length) {
        autoTable(doc, {
          head: [["Item Name", "Qty", "Rate", "Amount"]],
          body: relatedItems.map(item => [
            item.ItemName || "N/A",
            item.Qty || 0,
            (item.Rate || 0).toFixed(2),
            (item.Amount || 0).toFixed(2),
          ]),
          startY: currentY,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [230, 230, 230], textColor: [40, 40, 40] },
          theme: "grid"
        });

        currentY = doc.lastAutoTable.finalY + 4;
      }
    }
  });

  doc.save(`Ledger_${selectedParty?.VName || "Party"}.pdf`);
};


  const handleWhatsAppShare = async () => {
    if (!ledgerRef.current || !selectedParty?.Mobile) {
      alert('Ledger or mobile number is not available for sharing.');
      return;
    }
    const canvas = await html2canvas(ledgerRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    if (blob) await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
    const mobileNumber = '91' + selectedParty.Mobile.replace(/\D/g, '');
    const message = encodeURIComponent('Please find the attached ledger details.');
    window.open(`https://wa.me/${mobileNumber}?text=${message}`, '_blank');
  };
  
    // --- Render Method ---
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="w-full">

        {/* --- Input Form --- */}
        {!showResults && (
          <>
            <Heading>Account Ledger</Heading>
            {error && <Message text={error} type="error" />}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
  <label className="block mb-1 text-sm font-semibold text-gray-900">Party Type</label>
  <select
  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
  value={selectedPartyType}
  onChange={(e) => setSelectedPartyType(e.target.value)}
>
  {partyTypes.map((typeObj, idx) => (
    <option key={idx} value={typeObj.Vtype}>{typeObj.Vtype}</option>
  ))}
</select>
</div>


              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-900">Report Format</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={reportFormat}
                  onChange={(e) => setReportFormat(e.target.value)}
                >
                  <option value="Ledger Statement">Summary</option>
                  <option value="Detailed">Detailed</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-900">Select Customer</label>
                <div className="relative">
                  <input 
                    type="text" 
                    value={customerSearch} 
                    onChange={(e) => setCustomerSearch(e.target.value)} 
                    onFocus={() => setIsDropdownOpen(true)} 
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" 
                    placeholder="Type customer name..." 
                    autoComplete="off" 
                  />
                  {isDropdownOpen && filteredCustomers.length > 0 && (
                    <ul className="absolute z-10 w-full overflow-y-auto bg-white border rounded-md shadow max-h-48">
                      {filteredCustomers.map(c => (
                        <li key={c.Vid} onClick={() => handleCustomerSelect(c)} className="px-3 py-2 cursor-pointer hover:bg-gray-100">
                          <p className="font-semibold">{c.VName}</p>
                          <p className="text-xs text-gray-500">{c.Mobile}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-900">From Date</label>
                <input type="date" className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-900">To Date</label>
                <input type="date" className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>

              <div className="col-span-1 md:col-span-3 flex justify-center mt-2">
                <Button onClick={handleGenerate} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate'}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* --- Results View --- */}
        {showResults && (
          <>
            {loading && <Spinner />}

            {ledgerData.length > 0 && (
              <Panel title={<span className="block text-center w-full">Account Summary</span>}>
                <div className="flex justify-between items-center mb-3">
                  <button onClick={() => setShowResults(false)} className="flex items-center gap-1 px-3 py-1 rounded-md text-white text-xs font-medium bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 shadow-sm transition duration-200 ease-in-out">
                    Back
                  </button>
                  <div className="flex gap-2">
                    <button onClick={exportLedgerToPDF} disabled={ledgerData.length === 0} className="flex items-center gap-1 px-3 py-1 rounded-md text-white text-xs font-medium bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition duration-200 ease-in-out">
                      Export to PDF
                    </button>
                    <button onClick={handleWhatsAppShare} className="flex items-center gap-1 px-3 py-1 rounded-md text-white text-xs font-medium bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-sm transition duration-200 ease-in-out">
                      WhatsApp
                    </button>
                  </div>
                </div>

                <div ref={ledgerRef} className="p-6 bg-white rounded-md shadow-md border border-gray-200">
                  {/* Header */}
                  <div className="mb-4 text-center border-b pb-3">
                    <h1 className="text-2xl font-extrabold text-indigo-700 tracking-wide">{companyInfo.ComName || "Company Name"}</h1>
                    <p className="mt-1 text-xs text-gray-600">{companyInfo.Address1 || ""} {companyInfo.Address2 || ""}, {companyInfo.City || ""} - {companyInfo.Pincode || ""}, {companyInfo.State || ""}</p>
                  </div>
                  {/* Customer Info */}
                  <div className="flex justify-between mb-4 text-xs bg-gray-50 p-3 rounded border">
                    <div>
                      <p><span className="font-semibold text-gray-700">Customer:</span> {selectedParty?.VName}</p>
                      <p><span className="font-semibold text-gray-700">Mobile:</span> {selectedParty?.Mobile}</p>
                    </div>
                    <div className="text-right">
                      <p><span className="font-semibold text-gray-700">From:</span> {fromDate}</p>
                      <p><span className="font-semibold text-gray-700">To:</span> {toDate}</p>
                    </div>
                  </div>
                  {/* Summary Cards */}
                  {summaryData && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                      <div className="p-3 rounded-md shadow-sm bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200">
                        <p className="text-xs font-medium text-gray-600">Invoices</p>
                        <p className="mt-1 text-lg font-bold text-blue-700">₹{summaryData.Invoice.toFixed(2)}</p>
                      </div>
                      <div className="p-3 rounded-md shadow-sm bg-gradient-to-r from-green-50 to-green-100 border border-green-200">
                        <p className="text-xs font-medium text-gray-600">Receipts</p>
                        <p className="mt-1 text-lg font-bold text-green-700">₹{summaryData.Receipt.toFixed(2)}</p>
                      </div>
                      <div className="p-3 rounded-md shadow-sm bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200">
                        <p className="text-xs font-medium text-gray-600">Balance</p>
                        <p className="mt-1 text-lg font-bold text-yellow-700">₹{summaryData.Balance.toFixed(2)}</p>
                      </div>
                    </div>
                  )}

                  {/* --- Ledger Table --- */}
                  <div className="overflow-x-auto rounded-xl shadow-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-indigo-600 to-indigo-500 text-white">
                        <tr>
                          <th className="px-4 py-3 text-xs font-semibold text-left uppercase tracking-wide">Date</th>
                          <th className="px-4 py-3 text-xs font-semibold text-left uppercase tracking-wide">Particulars</th>
                          <th className="px-4 py-3 text-xs font-semibold text-right uppercase tracking-wide">Invoice</th>
                          <th className="px-4 py-3 text-xs font-semibold text-right uppercase tracking-wide">Receipt</th>
                          <th className="px-4 py-3 text-xs font-semibold text-right uppercase tracking-wide">Balance</th>
                        </tr>
                      </thead>
    <tbody className="divide-y divide-gray-100 bg-white">
  {ledgerData.map((row, idx) => {
    const relatedItems = itemDetails.filter(item => item.Cpid === row.Cpid);

    return (
      <React.Fragment key={idx}>
        {/* Ledger Row */}
        <tr className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}>
          <td className="px-4 py-3 text-sm text-gray-700">{row.RefDate}</td>
          <td className="px-4 py-3 text-sm text-gray-700">
            {row.RefNo} / {row.RefType} / {row.Particulars}
          </td>
          <td className="px-4 py-3 text-sm text-right text-blue-700">
            {row.Invoice.toFixed(2)}
          </td>
          <td className="px-4 py-3 text-sm text-right text-green-700">
            {parseFloat(row.Receipt).toFixed(2)}
          </td>
          <td className="px-4 py-3 text-sm font-semibold text-right text-indigo-700">
            {row.Balance.toFixed(2)}
          </td>
        </tr>

        {/* Item Rows */}
        {reportFormat === "Detailed" &&
          row.RefType === "Invoice" &&
          relatedItems.length > 0 && (
            <>
              {/* Header row for items */}
              <tr className="bg-indigo-100 text-indigo-800 text-xs font-semibold">
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2">Item Name</td>
                <td className="px-4 py-2 text-right">Qty</td>
                <td className="px-4 py-2 text-right">Rate</td>
                <td className="px-4 py-2 text-right">Amount</td>
              </tr>

              {/* Item detail rows */}
              {relatedItems.map((item, itemIdx) => (
                <tr key={itemIdx} className="bg-indigo-50 text-xs">
                  <td className="px-4 py-1"></td>
                  <td className="px-4 py-1 text-gray-600">{item.ItemName || "N/A"}</td>
                  <td className="px-4 py-1 text-right text-gray-600">{item.Qty || 0}</td>
                  <td className="px-4 py-1 text-right text-gray-600">{(item.Rate || 0).toFixed(2)}</td>
                  <td className="px-4 py-1 text-right text-gray-600">{(item.Amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </>
          )}
      </React.Fragment>
    );
  })}
</tbody>


                </table>
                  </div>
                </div>
              </Panel>
            )}

            {!loading && ledgerData.length === 0 && (
              <div className="p-10 text-center text-gray-500">
                No ledger data available. Please select a party and date range, then click "Generate".
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

